import { data } from 'react-router'
import { z } from 'zod'
import { LandingPageRenderer } from '#app/components/landings/renderers/LandingPageRenderer'
import { loadLandingPageContent } from '#app/config/landing/landingContent.config'
import {
	type CourseError,
	getFeaturedCollections,
	getFeaturedCourses,
} from '#app/domains/courses/index.ts'
import {
	createEffectErrorHandler,
	createErrorLogger,
} from '#app/domains/shared/handlers/effectErrorBoundary.server'
import { getImperativeLogger } from '#app/domains/shared/infrastructure/loggerRuntime'
import { errorToResponse } from '#app/domains/shared/services/errorMapper.server'
import { getUserId } from '#app/utils/auth.server.ts'
import { cache, cachified } from '#app/utils/cache.server.ts'
import { runWithDI } from '#app/utils/di/withDI'
import { pipeHeaders } from '#app/utils/headers.server'
import { getBrandPricingContent } from '#app/utils/pz/billing/pricingContent.server'
import { applyCollectionEntityToPath } from '#app/utils/pz/brand/collectionEntity'
import { buildPublicMarketingCacheHeaders } from '#app/utils/pz/http/cachePolicy.server'
import {
	enhanceMarketingHomeLanding,
	getPriorityHomeCollection,
	prioritizeHomeFeaturedCollections,
	type FeaturedHomeCollection,
} from '#app/utils/pz/landing/marketingHome'
import { getResolvedLeadMagnet } from '#app/utils/pz/leadMagnet/leadMagnet.server'
import { buildLeadMagnetSignupTarget } from '#app/utils/pz/leadMagnet/targeting'
import {
	getMarketingHomeAnonymousCtaTargetPath,
	getMarketingHomeCtaTargets,
	shouldPrioritizeMarketingHomeLeadMagnet,
} from '#app/utils/pz/marketing/acquisitionPolicy'
import { path } from '#app/utils/pz/routes.generated'
import { type Route } from './+types/index'

// ============================================================================
// Error Handler Configuration
// ============================================================================

const homeLogger = createErrorLogger('[Home]')
const homeRuntimeLogger = getImperativeLogger('[Home]')
const HOME_FEATURED_DATA_TIMEOUT_MS = 2500

type MarketingHomeFeaturedData = {
	featuredCourses: unknown[]
	featuredCollections: FeaturedHomeCollection[]
	priorityCollectionId: string | null
	collectionsDiscoveryHrefOverride: string | null
}

const EMPTY_HOME_FEATURED_DATA: MarketingHomeFeaturedData = {
	featuredCourses: [],
	featuredCollections: [],
	priorityCollectionId: null,
	collectionsDiscoveryHrefOverride: null,
}

async function withHomeFeaturedDataTimeout(
	promise: Promise<MarketingHomeFeaturedData>,
	brandKey: string,
): Promise<MarketingHomeFeaturedData> {
	let timeoutId: ReturnType<typeof setTimeout> | undefined

	try {
		return await Promise.race([
			promise,
			new Promise<MarketingHomeFeaturedData>((resolve) => {
				timeoutId = setTimeout(() => {
					homeRuntimeLogger.warn('Home featured data timed out', {
						brandKey,
						timeoutMs: HOME_FEATURED_DATA_TIMEOUT_MS,
					})
					resolve(EMPTY_HOME_FEATURED_DATA)
				}, HOME_FEATURED_DATA_TIMEOUT_MS)
			}),
		])
	} catch (error) {
		homeRuntimeLogger.warn('Home featured data failed', {
			brandKey,
			error: error instanceof Error ? error.message : String(error),
		})
		return EMPTY_HOME_FEATURED_DATA
	} finally {
		if (timeoutId) clearTimeout(timeoutId)
	}
}

const homeErrorHandler = createEffectErrorHandler({
	errorMapper: errorToResponse,
	logger: homeLogger,
})

export const meta: Route.MetaFunction = ({ loaderData }) => {
	const title =
		loaderData?.landingPageData.root?.title ??
		'Arkeias - Todo lo que aprendes sobre arquitectura'
	const description =
		loaderData?.landingPageData.root?.description ??
		'Aprende arquitectura con estructura, continuidad y un sistema al que siempre puedes volver.'

	return [{ title }, { name: 'description', content: description }]
}

export async function loader({ request }: Route.LoaderArgs) {
	try {
		const { resolveBrandConfig } = await import(
			'#app/config/brands/brands.config'
		)
		const brandConfig = resolveBrandConfig()
		const { key: brandKey } = brandConfig
		const personalMainEntityCardImage =
			brandConfig.marketing?.landing?.personalMainEntityCardImage ?? null
		const leadMagnet = await getResolvedLeadMagnet({ candidate: brandKey })
		const leadMagnetCollectionId = leadMagnet.effectiveCollectionId
		const userId = await getUserId(request)
		const prioritizeLeadMagnetCollection =
			shouldPrioritizeMarketingHomeLeadMagnet(brandKey)
		const pricingContent = await getBrandPricingContent(brandKey)

		// Load landing page content from brand-specific JSON file
		const landingPageData = await loadLandingPageContent(brandKey, 'home')

		if (!landingPageData) {
			throw new Response('Not Found', { status: 404 })
		}

		const cachedHomeData = await withHomeFeaturedDataTimeout(
			cachified({
				key: `marketing-home:${brandKey}:featured:v5`,
				cache,
				ttl: 1000 * 60,
				swr: 1000 * 60 * 5,
				async getFreshValue() {
					const featuredCourses = await runWithDI(getFeaturedCourses(12))
					const rawFeaturedCollections = await runWithDI(
						getFeaturedCollections(8),
					)
					const featuredCollections = prioritizeLeadMagnetCollection
						? prioritizeHomeFeaturedCollections(
								rawFeaturedCollections,
								leadMagnetCollectionId,
							)
						: rawFeaturedCollections
					const priorityCollection = prioritizeLeadMagnetCollection
						? getPriorityHomeCollection(
								featuredCollections,
								leadMagnetCollectionId,
							)
						: null
					const collectionsDiscoveryHrefOverride = priorityCollection?.link
						? applyCollectionEntityToPath(priorityCollection.link)
						: null

					return {
						featuredCourses,
						featuredCollections,
						priorityCollectionId: priorityCollection?.id ?? null,
						collectionsDiscoveryHrefOverride,
					}
				},
				checkValue: z.object({
					featuredCourses: z.array(z.any()),
					featuredCollections: z.array(z.any()),
					priorityCollectionId: z.string().nullable(),
					collectionsDiscoveryHrefOverride: z.string().nullable(),
				}),
			}),
			brandKey,
		)

		return data(
			{
				brandKey,
				landingPageData,
				featuredCourses: cachedHomeData.featuredCourses,
				featuredCollections: cachedHomeData.featuredCollections,
				priorityCollectionId: cachedHomeData.priorityCollectionId,
				collectionsDiscoveryHrefOverride:
					cachedHomeData.collectionsDiscoveryHrefOverride,
				isAuthenticated: Boolean(userId),
				pricingContent,
				personalMainEntityCardImage,
			},
			{
				headers: buildPublicMarketingCacheHeaders(),
			},
		)
	} catch (error) {
		if (error instanceof Response) throw error
		throw homeErrorHandler(error as CourseError)
	}
}

export const headers = pipeHeaders

export default function MarketingHomePage({
	loaderData,
}: Route.ComponentProps) {
	const data = loaderData
	const {
		landingPageData,
		featuredCourses = [],
		featuredCollections = [],
		priorityCollectionId,
		collectionsDiscoveryHrefOverride,
		brandKey,
		isAuthenticated,
		pricingContent,
		personalMainEntityCardImage,
	} = data

	if (!landingPageData) {
		return null
	}

	const anonymousCtaTargetPath = getMarketingHomeAnonymousCtaTargetPath({
		brandKey,
		leadMagnetSignupPath: buildLeadMagnetSignupTarget({
			leadMagnet: {
				enabled: Boolean(priorityCollectionId),
				collectionId: priorityCollectionId,
			},
		}),
	})
	const ctaTargetPath = isAuthenticated
		? collectionsDiscoveryHrefOverride || path('collections.base')
		: anonymousCtaTargetPath
	const ctaTargets = getMarketingHomeCtaTargets({
		brandKey,
		isAuthenticated,
	})

	const enhancedPageData = enhanceMarketingHomeLanding({
		landingPageData,
		featuredCourses,
		featuredCollections: featuredCollections as FeaturedHomeCollection[],
		ctaTargetPath,
		collectionsDiscoveryHrefOverride,
		ctaTargets,
		pricingContent,
		personalMainEntityCardImage,
	})

	return (
		<div className="bg-background text-primary">
			<LandingPageRenderer data={enhancedPageData} />
		</div>
	)
}
